import React from "react";
import {
  CaretLeft,
  Megaphone,
  ClockCounterClockwise,
} from "@phosphor-icons/react";
import { Button } from "flowbite-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import AppLogo from "../../../AppCountryReports/components/AppLogo";
import DateRangePickerButton from "../../../../components/DateRangePicker/DateRangePickerButton";
import DynamicFilter from "../../../../components/DynamicFilter";
import SearchInput from "../../../../components/SearchInput";
import FilterSummary from "../../../../components/FilterSummary";
import { CampaignHeaderProps } from "../../interface/shared";

const CampaignSharedHeader: React.FC<CampaignHeaderProps> = ({
  title,
  platform,
  type,
  onBack,
  onSearch,
  searchTerm,
  onFilterApply,
  filterFields,
  activeFilters,
  onClearAllFilters,
  onHistoryClick,
  onDateRangeChange,
  onPresetChange,
  dateRange,
  selectedPresetId,
  pillSwitch,
  appInfo,
  searchPlaceholder,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate(-1);
    }
  };

  const renderIcon = () => {
    if (type === "list") {
      return <Megaphone size={20} className="text-gray-700" weight="duotone" />;
    }
    return (
      <button
        onClick={handleBack}
        className="hover:bg-gray-100 rounded-lg transition-colors flex items-center justify-center p-1 -ml-1 text-gray-700"
      >
        <CaretLeft size={20} weight="bold" />
      </button>
    );
  };

  const renderTitleAndAppInfo = () => {
    if (type === "list") {
      return (
        <div className="flex items-center gap-2">
          {renderIcon()}
          <h1 className="text-md font-bold text-gray-700">{title}</h1>
        </div>
      );
    }

    // Detail or History
    return (
      <div className="flex items-center gap-3">
        {renderIcon()}
        {appInfo && (
          <div className="flex-shrink-0 w-12 h-12 flex justify-center items-center border border-gray-200 dark:border-gray-800 rounded-lg bg-gray-50 dark:bg-gray-900/50 shadow-sm overflow-hidden p-1">
            <AppLogo
              logoUrl={appInfo.logoUrl || null}
              platform={platform === "apple_ads" ? "ios" : platform}
              appName={appInfo.name}
              className="w-full h-full object-contain"
            />
          </div>
        )}
        <div className="flex flex-col min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-md font-bold text-gray-700 truncate max-w-[300px] md:max-w-md">
              {title}
            </h1>
            {type === "history" && (
              <div className="px-2 py-0 bg-blue-50 text-blue-600 rounded-md font-bold uppercase tracking-wider text-[10px]">
                {t("campaigns.history.subtitle", "History")}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <p className="text-[11px] text-gray-400 font-medium truncate -mt-0.5">
              ID: <span className="text-gray-700">{appInfo?.id}</span>
            </p>
            {appInfo?.name && type === "detail" && (
              <>
                <span className="text-[11px] text-gray-400 font-medium">|</span>
                <p className="text-[11px] text-gray-400 font-medium truncate -mt-0.5">
                  App: <span className="text-gray-700">{appInfo.name}</span>
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Unified Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="pl-1">{renderTitleAndAppInfo()}</div>
        <div className="w-auto flex items-center gap-2 flex-wrap">
          {pillSwitch}
          {onFilterApply && filterFields && (
            <DynamicFilter
              title={t("common.filter")}
              onApply={onFilterApply}
              fields={filterFields}
            />
          )}
          {type === "detail" && onHistoryClick && (
            <Button color="primary" size="sm" onClick={onHistoryClick}>
              <div className="flex items-center gap-2 px-1">
                <ClockCounterClockwise size={18} />
                <span className="font-medium">
                  {t("common.viewHistory", "View History")}
                </span>
              </div>
            </Button>
          )}
          <DateRangePickerButton
            onDateRangeChange={onDateRangeChange}
            onPresetChange={onPresetChange}
            initialStartDate={dateRange.startDate}
            initialEndDate={dateRange.endDate}
            initialPreset={selectedPresetId}
          />
          {onSearch && (
            <SearchInput
              onSearch={onSearch}
              placeholder={searchPlaceholder || t("common.search")}
              className="w-[200px]"
              style={{ height: "38px" }}
              size="md"
              initialValue={searchTerm}
            />
          )}
        </div>
      </div>

      {activeFilters && activeFilters.length > 0 && onClearAllFilters && (
        <FilterSummary
          filters={activeFilters}
          onClearAll={onClearAllFilters}
          title={t("common.appliedFilters")}
          clearAllLabel={t("common.resetAllFilters") || "Clear All"}
        />
      )}
    </div>
  );
};

export default CampaignSharedHeader;
